import axios, { AxiosError, AxiosRequestConfig } from 'axios';

type CustomAxiosRequestConfig = AxiosRequestConfig & {
    _retry?: boolean;
    headers?: Record<string, any>; // indexation dynamique
};

const apiClient = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true, // will include cookies by default (helpful)
});

let isRefreshing = false;
let failedQueue: Array<{ resolve: (token?: string) => void; reject: (err: any) => void }> = [];

const processQueue = (error: AxiosError | null, token?: string | null) => {
    failedQueue.forEach((p) => {
        if (error) p.reject(error);
        else p.resolve(token || undefined);
    });

    failedQueue = [];
};

apiClient.interceptors.request.use((config) => {
    const token = sessionStorage.getItem('token');
    if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
});

apiClient.interceptors.response.use(
    (res) => res,
    async (err: AxiosError & { config?: AxiosRequestConfig & { _retry?: boolean } }) => {
        const originalRequest = err.config as CustomAxiosRequestConfig;
        if (!originalRequest) return Promise.reject(err);

        if (err.response?.status === 401 && !originalRequest._retry) {
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then((token) => {
                    if (!originalRequest.headers) originalRequest.headers = {};
                    originalRequest.headers['Authorization'] = 'Bearer ' + token;
                    return axios(originalRequest);
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const refreshRes = await axios.post(`${process.env.REACT_APP_API_URL?.replace(/\/$/, '')}/api/auth/refresh-token`, {}, { withCredentials: true });
                const newToken = (refreshRes.data.token || refreshRes.data.Token) as string;
                sessionStorage.setItem('token', newToken);
                apiClient.defaults.headers.common['Authorization'] = 'Bearer ' + newToken;
                processQueue(null, newToken);
                isRefreshing = false;

                if (!originalRequest.headers) originalRequest.headers = {};
                originalRequest.headers['Authorization'] = 'Bearer ' + newToken;
                return axios(originalRequest);
            } catch (refreshErr) {
                processQueue(refreshErr as AxiosError, null);
                isRefreshing = false;
                // redirect to login or throw depending on app flow
                window.location.href = '/login';
                return Promise.reject(refreshErr);
            }
        }

        if (err.response?.status === 403) {
            window.location.href = '/forbidden';
        }

        return Promise.reject(err);
    }
);

export default apiClient;
